import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, Select, InputNumber, Button, Row, Col } from "antd";
import { _get } from "../../Utils/Helper";
import { studentType } from "../../../core/common/selectoption/selectoption";

// Define the props type for the component
interface FeesSetupModalProps {
  visible: boolean;
  onClose: () => void;
  handleEdit: (menu: any) => void,
  onSubmit: (formData: FeeFormValues) => void;
  initialValues?: FeeFormValues; // Include initialValues to support editing
  onDelete?: (id: string) => void; // Callback for handling deletion
}

interface Class {
  id: string;
  class_group: string;
  level: string;
  section: string;
}

// Define the type for the form values
interface FeeFormValues {
  id?: string;
  code: string;
  status: string;
  description: string;
  section: string;
  student_type: string;
  amount: string;
  term: string;
  revenue_type: string;
  class_name: string;
  is_optional: string;
  school_id: number;
}

const FeesSetupModal: React.FC<FeesSetupModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  handleEdit,
  onDelete
}) => {
  const [form] = Form.useForm<FeeFormValues>();

  const [classes, setClasses] = useState<Class[]>([]);

  useEffect(() => {
    // Fetch available classes for selection
    _get(`classes?query_type=select-all`,
      (res) => {
        setClasses(res.data);
      },
      (err) => {
        console.log(err);
      }
    );
  }, []);

  // Extract unique sections from the classes data
  const unique_sections: string[] = [...new Set(classes?.map((item: Class) => item.section))];

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues); 
    }
  }, [initialValues, form]);

  const handleFinish = (values: FeeFormValues) => {
    onSubmit(values); 
    form.resetFields(); 
    onClose(); 
  };

  const handleDelete = () => {
    if (initialValues && onDelete) {
      onDelete(initialValues.code);
      onClose(); 
    }
  };

  return (
    <Modal
      title={initialValues && initialValues?.code ? "Edit Fee Details" : "Setup Fee Details"}
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    // size='lg'
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          discount: 0,
          fine: 0,
        }}
      >
        <Row>
          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Description is required" }]}
            >
              <Input placeholder="Enter fee description" />
            </Form.Item>
          </Col>

          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Amount (NGN)"
              name="amount"
              rules={[{ required: true, message: "Amount is required, minimun NGN 100" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={100}
                max={999999}
                placeholder="Enter amount"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Revenue Category"
              name="revenue_type"
              rules={[{ required: true, message: "Revenue Category is required" }]}
            >
              <Select placeholder="Select Category">
                <Select.Option value="Fees">Fees</Select.Option>
                <Select.Option value="Fines">Fines</Select.Option>
                <Select.Option value="Sales">Sales</Select.Option>
                <Select.Option value="Earnings">Earnings</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Section"
              name="section"
              rules={[{ required: true, message: "Section is required" }]}
            >
              <Select placeholder="Select Section">
                <Select.Option value="">Section</Select.Option>
                {unique_sections?.map((section: any, idx: number) => (
                  <Select.Option key={idx} value={section}>
                    {section}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Student Type"
              name="student_type"
              rules={[{ required: true, message: "Student Type is required" }]}
            >
              <Select placeholder="Select Student Type">
                <Select.Option value="">Select</Select.Option>
                {studentType.map((item: any, idx: number) => (
                  <Select.Option key={idx} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Class name"
              name="class_name"
              rules={[{ required: true, message: "Class is required" }]}
            >
              <Select placeholder="Select Class">
                <Select.Option value="All Classes">All Classes</Select.Option>
                {classes?.map((classItem: any, idx: number) => (
                  <Select.Option key={idx} value={classItem.class_name}>
                    {classItem.class_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Period"
              name="term"
              rules={[{ required: true, message: "Period is required" }]}
            >
              <Select placeholder="Select Term">
                <Select.Option value="Each Term">Each Term</Select.Option>
                <Select.Option value="First Term">First Term</Select.Option>
                <Select.Option value="Second Term">Second Term</Select.Option>
                <Select.Option value="Third Term">Third Term</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col md={12} lg={12} sm={12} xs={12} >
            <Form.Item
              label="Is Optional"
              name="is_optional"
              rules={[{ required: true, message: "This is required" }]}
            >
              <Select placeholder="Select Option">
                {['Yes', 'No']?.map((label) => (
                  <Select.Option value={label} key={label}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="text-end mt-4" md={12} lg={12} sm={12} xs={12} >
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
                Submit
              </Button>
            </Form.Item>
          </Col>
          <Col md={12} lg={12} sm={12} xs={12} >
            {initialValues && initialValues.code ? (
              <>
                <Form.Item>
                  <Button
                    color="primary"
                    onClick={handleEdit}
                    style={{ width: "100%" }}
                  >
                    Edit
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleDelete}
                    style={{ width: "100%" }}
                  >
                    Delete
                  </Button>
                </Form.Item>
              </>) : null}
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default FeesSetupModal;
